import html2canvas from "html2canvas";
import _ from "lodash";
import { API_URL } from "@/config/env";

export const baseApiUrl = API_URL;
export const apiUrls = {
	countryListUrl: baseApiUrl + "GetUserCountries?userid=",
	GetScenarioforQuotaDefinition:
		baseApiUrl + "Define/GetScenarioforQuotaDefinition?countryId=",
	GetQuotaPeriods: baseApiUrl + "Define/GetQuotaPeriods?scenarioId=",
	GetDepletionModels: baseApiUrl + "Define/GetDepletionModels",
	GetDepletionPeriodTypes: baseApiUrl + "Define/GetDepletionPeriodTypes",
	GetRounderTypes: baseApiUrl + "Define/GetRounderTypes",
	GetWeeklyDepletionValues: baseApiUrl + "Define/GetWeeklyDepletionValues",
	GetSKUOrPGByScenario: baseApiUrl + "Define/GetSKUOrPGByScenario?ScenarioID=",
	GetQuotaDetails: baseApiUrl + "Define/GetQuotaDetails?",
	GetQuotaChartData: baseApiUrl + "Define/GetQuotaChartData",
	SaveQuota: baseApiUrl + "Define/SaveQuota",
	SubmitQuota: baseApiUrl + "Define/SubmitQuota",
	ExportExcel: baseApiUrl + "Define/ExportExcel",
	ImportExcel: baseApiUrl + "Define/ImportExcel",
	GetDSAData: baseApiUrl + "Scenario/GetDataSourceAlignmentData?",
	exportFile: baseApiUrl + "Define/ExportFile?",
	Upload: baseApiUrl + "Approve/Upload",
	Downloadfile: baseApiUrl + "Define/DownloadFile?",
};

export const views = [
	{ key: 1, value: "All" },
	{ key: 2, value: "SKU Name, SKU No, Cust Name, Cust No, Quota" },
];

export function catchElement(className) {
	const element = document.querySelector(className);
	if (!element) {
		console.error("Element not found");
		return;
	}
	// console.log("Element", element);
	// Detailed element diagnostics
	console.group("Element Capture Diagnostics");
	console.log("Element:", element);
	console.log("Computed Style:", window.getComputedStyle(element));
	console.log("Scroll Width:", element.scrollWidth);
	console.log("Scroll Height:", element.scrollHeight);
	console.log("Client Width:", element.clientWidth);
	console.log("Client Height:", element.clientHeight);
	console.log("Offset Width:", element.offsetWidth);
	console.log("Offset Height:", element.offsetHeight);
	console.log("Device Pixel Ratio:", window.devicePixelRatio);
	console.groupEnd();
	return element;
}

export async function elementToBase64(element) {
	try {
		const canvas = await html2canvas(element);
		return canvas.toDataURL("image/png");
	} catch (error) {
		console.error(
			"An error occurred while converting the element to base64:",
			error
		);
		return null;
	}
}

/**
 * Row shape when API omits per-customer depletion — mirrors Time Depletion in DefineQuota.
 * Returns only `depletionModelID` and `depletionModel` for grid rows (display names live on `depletionModelsList`).
 */
export function getTimeDepletionGridDefaults(selectedDepletion, depletionModelsList) {
	if (
		!selectedDepletion ||
		selectedDepletion.key === undefined ||
		selectedDepletion.key === null ||
		!depletionModelsList ||
		depletionModelsList.length === 0
	) {
		return null;
	}
	const m = depletionModelsList.find(
		(x) => x.depletionModel === selectedDepletion.key
	);
	if (!m) return null;
	return {
		depletionModelID: m.depletionModelID,
		depletionModel: m.depletionModel,
	};
}

/** Normalizes grid row depletion key (matches Time Depletion / DepletionModels.DepletionModel). */
export function rowDepletionModelKey(row) {
	const dm = row?.depletionModel;
	if (dm === undefined || dm === null) return "";
	return String(dm).trim().toLowerCase();
}

function roundToNearestMultiple(number, roundTo) {
	if (!roundTo || roundTo === 0) {
		return Math.round(number);
	}
	return roundTo * Math.round(number / roundTo);
}

/**
 * OverBox rounder for weekly and biweekly depletion rows (not none).
 * Weekly: divides quota by 4 or 5, rounds the slice, cumulative weeks 1..(n-1),
 * final week = full quota.
 * Biweekly: divides quota by 2, rounds week1 only; week2 = full quota (final week).
 * @returns {boolean} true when the row was updated
 */
export function buildSkuRounderDetailsList(overBoxRoundValues) {
	if (!overBoxRoundValues) {
		return [];
	}

	return Object.entries(overBoxRoundValues)
		.filter(
			([, rounder]) =>
				rounder != null && rounder !== "" && Number(rounder) > 0
		)
		.map(([skuid, rounder]) => ({
			skuid: Number(skuid),
			rounder: Number(rounder),
		}));
}

export function overBoxRoundValuesFromSkuRounderDetailsList(skuRounderDetailsList) {
	const result = {};
	(skuRounderDetailsList ?? []).forEach((item) => {
		const skuid = item?.skuid ?? item?.SKUID;
		const rounder = item?.rounder ?? item?.Rounder;
		if (skuid != null && rounder != null && Number(rounder) > 0) {
			result[skuid] = Number(rounder);
		}
	});
	return result;
}

export function overBoxRounderTypeFromList(rounderTypesList) {
	return (rounderTypesList ?? []).find((row) =>
		String(row.rounderType ?? "").toLowerCase().includes("overbox")
	);
}

export function standardRounderTypeFromList(rounderTypesList) {
	const list = rounderTypesList ?? [];
	return (
		list.find(
			(row) =>
				!String(row.rounderType ?? "").toLowerCase().includes("overbox")
		) ?? list[0]
	);
}

export function isOverBoxRounderTypeSelected(rounderTypesList, selectedRounderTypeID) {
	const selected = (rounderTypesList ?? []).find(
		(row) => String(row.rounderTypeID) === String(selectedRounderTypeID)
	);
	return String(selected?.rounderType ?? "")
		.toLowerCase()
		.includes("overbox");
}

export function applyOverBoxRounderToRow(item, overBoxRoundValue, isFourWeekPeriod) {
	const depletionKey = rowDepletionModelKey(item);
	if (!depletionKey || depletionKey === "none") {
		return false;
	}

	let quota = item.quota;
	if (typeof quota === "string") {
		quota = parseFloat(quota);
	}
	if (!Number.isFinite(quota) || quota < overBoxRoundValue) {
		return false;
	}

	const capAtQuota = (value) => Math.min(value, quota);

	if (depletionKey === "biweekly") {
		const halfSlice = quota / 2;
		const roundedHalf = roundToNearestMultiple(halfSlice, overBoxRoundValue);
		if (roundedHalf === 0) {
			item.weekly1 = overBoxRoundValue;
		} else {
			item.weekly1 = capAtQuota(roundedHalf);
		}
		item.weekly2 = quota;
		item.weekly3 = 0;
		item.weekly4 = 0;
		item.weekly5 = 0;
		item.revisedQuota = quota;
		return true;
	}

	const weekCount = isFourWeekPeriod ? 4 : 5;
	const weeklySlice = quota / weekCount;
	const roundedSlice = roundToNearestMultiple(weeklySlice, overBoxRoundValue);

	if (roundedSlice === 0) {
		item.weekly1 = overBoxRoundValue;
		for (let week = 2; week <= weekCount; week++) {
			item[`weekly${week}`] = quota;
		}
	} else {
		for (let week = 1; week < weekCount; week++) {
			item[`weekly${week}`] = capAtQuota(roundedSlice * week);
		}
		if (isFourWeekPeriod) {
			item.weekly4 = quota;
		} else {
			item.weekly4 = capAtQuota(roundedSlice * 4);
			item.weekly5 = quota;
		}
	}

	if (isFourWeekPeriod) {
		item.weekly5 = 0;
	}

	item.revisedQuota = quota;
	return true;
}

/** Latest grid row for customer/SKU keys — use inside debounced handlers instead of stale params.row. */
export function getGridRowCompositeKey(row) {
	if (!row) return "";
	return `${row.customerID}-${row.customerGroupID}-${row.productGroupID}-${row.skuid}`;
}

export function findGridRowByCompositeKey(gridRows, keyRow) {
	if (!Array.isArray(gridRows) || !keyRow) return null;
	const key = getGridRowCompositeKey(keyRow);
	return gridRows.find((item) => getGridRowCompositeKey(item) === key) ?? null;
}

/** Quota/week fields preserved before OverBox rounder for normal rounder restore. */
export const PRE_ROUNDER_BACKUP_SYNC_FIELDS = [
	"quota",
	"revisedQuota",
	"weekly1",
	"weekly2",
	"weekly3",
	"weekly4",
	"weekly5",
	"adjustment",
	"safetyMargin",
	"tender",
];

export function copyPreRounderFieldsToTarget(target, source) {
	if (!target || !source) return;
	for (const field of PRE_ROUNDER_BACKUP_SYNC_FIELDS) {
		if (Object.prototype.hasOwnProperty.call(source, field)) {
			target[field] = source[field];
		}
	}
}

export function syncPreRounderBackupRows(backup, sourceRows) {
	if (!Array.isArray(backup) || !backup.length || !Array.isArray(sourceRows)) {
		return;
	}
	const backupByKey = new Map(
		backup.map((row) => [getGridRowCompositeKey(row), row])
	);
	for (const row of sourceRows) {
		const backupRow = backupByKey.get(getGridRowCompositeKey(row));
		if (backupRow) {
			copyPreRounderFieldsToTarget(backupRow, row);
		}
	}
}

/** Sync only the fields that were manually edited on one row. */
export function syncPreRounderBackupFieldsFromRow(backup, sourceRow, fields) {
	if (
		!Array.isArray(backup) ||
		!backup.length ||
		!sourceRow ||
		!Array.isArray(fields) ||
		!fields.length
	) {
		return;
	}
	const backupRow = findGridRowByCompositeKey(backup, sourceRow);
	if (!backupRow) return;
	for (const field of fields) {
		if (Object.prototype.hasOwnProperty.call(sourceRow, field)) {
			backupRow[field] = sourceRow[field];
		}
	}
}

export const DEPLETION_TYPE_BACKUP_FIELDS = [
	...PRE_ROUNDER_BACKUP_SYNC_FIELDS,
	"depletionModel",
	"depletionModelID",
	"depletionModelName",
	"weekly1Per",
	"weekly2Per",
	"weekly3Per",
	"weekly4Per",
	"weekly5Per",
];

/** Persist grid edits and optionally sync specific fields to pre-rounder backup. */
export function commitGridDataUpdate(dispatch, setStateAction, payload) {
	const { gridData, gridDataFiltered, updatedRow, backupFields } = payload;
	dispatch(setStateAction("gridDataFiltered", gridDataFiltered));
	const options =
		updatedRow && backupFields?.length
			? { preRounderBackupSync: { row: updatedRow, fields: backupFields } }
			: undefined;
	dispatch(setStateAction("gridData", gridData, options));
}

export function mergeFilteredRowsWithPreRounderBackup(filteredRows, backupRows) {
	if (!Array.isArray(filteredRows)) return [];
	if (!Array.isArray(backupRows) || !backupRows.length) {
		return filteredRows.map((row) => ({ ...row }));
	}
	return filteredRows.map((row) => {
		const backupRow = findGridRowByCompositeKey(backupRows, row);
		if (!backupRow) {
			return { ...row };
		}
		const merged = { ...row };
		copyPreRounderFieldsToTarget(merged, backupRow);
		return merged;
	});
}

export function findGridRowByCustomerSku(gridRows, keyRow) {
	if (!Array.isArray(gridRows) || !keyRow) return null;
	const {
		customerID,
		customerGroupID,
		productGroupID,
		skuid,
	} = keyRow;
	return (
		gridRows.find(
			(item) =>
				item.customerID === customerID &&
				item.customerGroupID === customerGroupID &&
				item.productGroupID === productGroupID &&
				item.skuid === skuid
		) ?? null
	);
}

/** Curve fractions for depletion "none": 100% week 1 (avoids all-zero % columns). */
export const NONE_DEPLETION_WEEK_CURVE = Object.freeze({
	week1: 1,
	week2: 0,
	week3: 0,
	week4: 0,
	week5: 0,
});

/** Grid `weekly*NPer` fields for none depletion (same meaning as {@link NONE_DEPLETION_WEEK_CURVE}). */
export const NONE_DEPLETION_GRID_WEEK_PERCENTS = Object.freeze({
	weekly1Per: 1,
	weekly2Per: 0,
	weekly3Per: 0,
	weekly4Per: 0,
	weekly5Per: 0,
});

/** Normalize API row from GetWeeklyDepletionValues (camelCase or PascalCase). */
export function weeklyRowToPercentPayload(row) {
	if (!row || typeof row !== "object") {
		return { week1: 0, week2: 0, week3: 0, week4: 0, week5: 0 };
	}
	const n = (v) =>
		v === undefined || v === null || v === "" ? 0 : Number(v);
	return {
		week1: n(row.week1 ?? row.Week1),
		week2: n(row.week2 ?? row.Week2),
		week3: n(row.week3 ?? row.Week3),
		week4: n(row.week4 ?? row.Week4),
		week5: n(row.week5 ?? row.Week5),
	};
}

function rowHasMeaningfulWeek5(row) {
	const v = row?.week5 ?? row?.Week5;
	return v != null && !Number.isNaN(Number(v)) && Number(v) !== 0;
}

/**
 * Pick one SP row per WeeklyDepletionType when the ref returns 4-week vs 5-week duplicates.
 */
export function pickWeeklyDepletionRow(valuesList, depletionModelKey, periodTypeRow) {
	if (!valuesList || valuesList.length === 0) return null;
	const k = String(depletionModelKey ?? "")
		.trim()
		.toLowerCase();
	if (!k || k === "none") return null;
	const candidates = valuesList.filter((x) => {
		const t = String(x.weeklyDepletionType ?? x.WeeklyDepletionType ?? "")
			.trim()
			.toLowerCase();
		return t === k;
	});
	if (candidates.length === 0) return null;
	if (candidates.length === 1) return candidates[0];
	const showFive = depletionPeriodTypeShowsFiveWeeks(periodTypeRow);
	if (showFive) {
		const withW5 = candidates.find((r) => rowHasMeaningfulWeek5(r));
		return withW5 ?? candidates[0];
	}
	const withoutW5 = candidates.find((r) => !rowHasMeaningfulWeek5(r));
	return withoutW5 ?? candidates[0];
}

/** Percent curve for one grid row from ref list (fallback: existing weeklyNPer on row). For depletion `none`, returns 100% week 1. */
export function getRowWeekPercents(weeklyDepletionValuesList, selectedDepletionPeriodType, row) {
	const dk = rowDepletionModelKey(row);
	if (dk === "none") {
		return { ...NONE_DEPLETION_WEEK_CURVE };
	}
	const picked = pickWeeklyDepletionRow(
		weeklyDepletionValuesList,
		dk,
		selectedDepletionPeriodType
	);
	if (picked) return weeklyRowToPercentPayload(picked);
	return {
		week1: Number(row?.weekly1Per ?? 0),
		week2: Number(row?.weekly2Per ?? 0),
		week3: Number(row?.weekly3Per ?? 0),
		week4: Number(row?.weekly4Per ?? 0),
		week5: Number(row?.weekly5Per ?? 0),
	};
}

/**
 * Split row quota into weekly1..weekly5 from depletion curve percents.
 * `wc` must be `{ week1, … week5 }` numeric fractions (e.g. from {@link getRowWeekPercents}).
 * When `periodTypeRow` is a 4-week period, weekly5 is always 0.
 * Matches setWeekPercentDepletion / adjustment-cell weekly logic.
 */
export function splitQuotaAcrossWeeks(quotaRaw, dk, wc, periodTypeRow) {
	const n = Number(quotaRaw);
	const q =
		quotaRaw === undefined || quotaRaw === null || quotaRaw === "" || !Number.isFinite(n)
			? 0
			: Math.round(n);
	const c = wc ?? {};
	const curve = {
		week1: Number(c.week1 ?? 0),
		week2: Number(c.week2 ?? 0),
		week3: Number(c.week3 ?? 0),
		week4: Number(c.week4 ?? 0),
		week5: Number(c.week5 ?? 0),
	};
	let w1 = 0;
	let w2 = 0;
	let w3 = 0;
	let w4 = 0;
	let w5 = 0;

	if (!dk || dk === "none") {
		w1 = q;
	} else if (dk === "biweekly") {
		w1 = Math.round(q * curve.week1);
		if (w1 > q) {
			w1 = q;
		}
		w2 = q;
	} else if (dk === "depletion_80_20") {
		w1 = Math.round(q * curve.week1);
		if (w1 > q) {
			w1 = q;
		}
		w2 = Math.round(q * curve.week2);
		if (w2 > q) {
			w2 = q;
		}
		w3 = Math.round(q * curve.week3);
		if (w3 > q) {
			w3 = q;
		}
		w4 = Math.round(q * curve.week4);
		if (w4 > q) {
			w4 = q;
		}
		if (q * curve.week5 !== 0) {
			w5 = q;
		} else {
			w4 = q;
			w5 = q;
		}
	} else {
		w1 = Math.round(q * curve.week1);
		if (w1 > q) {
			w1 = q;
		}
		w2 = Math.round(w1 + q * curve.week2);
		if (w2 > q) {
			w2 = q;
		}
		w3 = Math.round(w2 + q * curve.week3);
		if (w3 > q) {
			w3 = q;
		}
		w4 = Math.round(w3 + q * curve.week4);
		if (w4 > q) {
			w4 = q;
		}
		if (q * curve.week5 !== 0) {
			w5 = q;
		} else {
			w4 = q;
			w5 = q;
		}
	}

	return {
		weekly1: w1,
		weekly2: !dk || dk === "none" ? 0 : w2,
		weekly3: !dk || dk === "none" ? 0 : w3,
		weekly4: !dk || dk === "none" ? 0 : w4,
		weekly5:
			(periodTypeRow && depletionPeriodTypeIsFourWeek(periodTypeRow)) ||
			!dk ||
			dk === "none"
				? 0
				: w5,
	};
}

/** True when Period dropdown is explicitly 4-week (label contains "4-week"). */
export function depletionPeriodTypeIsFourWeek(periodTypeRow) {
	const label = String(
		periodTypeRow?.depletionPeriodType ?? ""
	).toLowerCase();
	return label.includes("4-week");
}

/**
 * Curve shape for splitQuotaAcrossWeeks from grid row weekly display percents.
 * Use after addWeeklyDepletionValuesToGrid so weekly1Per..weekly5Per match the reference list + period.
 */
export function weekPercentsCurveFromRow(row) {
	return {
		week1: Number(row?.weekly1Per ?? 0),
		week2: Number(row?.weekly2Per ?? 0),
		week3: Number(row?.weekly3Per ?? 0),
		week4: Number(row?.weekly4Per ?? 0),
		week5: Number(row?.weekly5Per ?? 0),
	};
}

/**
 * After `addWeeklyDepletionValuesToGrid` has set weekly1Per..weekly5Per on each row,
 * assign weekly1..weekly5 amounts from `row.quota`, depletion model, and period type.
 */
export function applyRowWeeklyBucketsFromQuota(row, selectedDepletionPeriodType) {
	const dk = rowDepletionModelKey(row);
	const rq = row.quota;
	if (!dk || dk === "none") {
		const n = Number(rq);
		const q =
			rq === undefined || rq === null || rq === "" || !Number.isFinite(n)
				? 0
				: Math.round(n);
		return {
			...row,
			weekly1: q,
			weekly2: 0,
			weekly3: 0,
			weekly4: 0,
			weekly5: 0,
		};
	}
	const wc = weekPercentsCurveFromRow(row);
	return {
		...row,
		...splitQuotaAcrossWeeks(rq, dk, wc, selectedDepletionPeriodType),
	};
}

/** Curve for global Time Depletion dropdown (adjustment cells, etc.). */
export function deriveWeeklyDepletionForSelected(
	weeklyDepletionValuesList,
	selectedDepletion,
	selectedDepletionPeriodType
) {
	const picked = pickWeeklyDepletionRow(
		weeklyDepletionValuesList,
		selectedDepletion?.key ?? "",
		selectedDepletionPeriodType
	);
	return weeklyRowToPercentPayload(picked);
}

/** True when every row has no depletion model or key "none" (at least one row). */
export function gridRowsAllDepletionNone(rows) {
	if (!rows || rows.length === 0) return false;
	return rows.every((r) => {
		const k = rowDepletionModelKey(r);
		return !k || k === "none";
	});
}

/** True when every row has depletion model key "biweekly". */
export function gridRowsAllBiweekly(rows) {
	if (!rows || rows.length === 0) return false;
	return rows.every((r) => rowDepletionModelKey(r) === "biweekly");
}

/** True when the grid has more than one distinct row depletion model (e.g. none + weekly). */
export function gridRowsHaveMultipleDepletionTypes(rows) {
	if (!rows || rows.length === 0) return false;
	const keys = new Set(rows.map((r) => rowDepletionModelKey(r)));
	return keys.size > 1;
}

/**
 * In a multi–depletion-type grid, week cells that are unused for `none` / `biweekly` rows
 * are shown as empty shells (no input): none → week 2+; biweekly → week 3+.
 */
export function shouldShowBlankWeekPlaceholder(weekNumber, row, rows) {
	if (!gridRowsHaveMultipleDepletionTypes(rows)) return false;
	const dm = rowDepletionModelKey(row);
	if (dm === "none") return weekNumber >= 2;
	if (dm === "biweekly") return weekNumber >= 3;
	return false;
}

/**
 * From Period dropdown row (DepletionPeriodTypes): whether weekly breakdown shows five week columns vs four (hide Week5).
 */
export function depletionPeriodTypeShowsFiveWeeks(periodTypeRow) {
	const label = String(
		periodTypeRow?.depletionPeriodType ?? ""
	).toLowerCase();
	if (label.includes("4-week")) return false;
	if (label.includes("5-week")) return true;
	return true;
}

/**
 * Per-cell edit permission for weekly quota breakdown (weekNumber 1–5).
 * Uses row `depletionModel` and Period dropdown `depletionPeriodType`.
 */
export function isWeekColumnEditable(
	weekNumber,
	row,
	selectedDepletionPeriodType
) {
	const dm = rowDepletionModelKey(row);
	if (dm === "none") return false;
	if (dm === "biweekly") return weekNumber === 1;

	const pt = String(
		selectedDepletionPeriodType?.depletionPeriodType ?? ""
	).toLowerCase();
	if (pt.includes("4-week")) {
		return weekNumber >= 1 && weekNumber <= 3;
	}
	if (pt.includes("5-week")) {
		return weekNumber >= 1 && weekNumber <= 4;
	}
	return weekNumber >= 1 && weekNumber <= 4;
}

function gridDepletionHasValue(v) {
	return v !== undefined && v !== null && String(v).trim() !== "";
}

function normalizePersistedBreakdownRowDepletion(dmList, snapRow) {
	if (!snapRow) return null;
	const rid = snapRow.depletionModelID;
	const rkey = snapRow.depletionModel;
	const hasEither =
		gridDepletionHasValue(rid) || gridDepletionHasValue(rkey);
	if (!hasEither) return null;
	if (!dmList?.length) {
		return {
			depletionModelID: rid ?? "",
			depletionModel: String(rkey ?? ""),
		};
	}
	if (gridDepletionHasValue(rid)) {
		const byId = dmList.find(
			(x) =>
				Number(x.depletionModelID ?? x.DepletionModelID) === Number(rid)
		);
		if (byId) {
			return {
				depletionModelID: byId.depletionModelID ?? byId.DepletionModelID,
				depletionModel: byId.depletionModel,
			};
		}
	}
	if (gridDepletionHasValue(rkey)) {
		const byKey = dmList.find(
			(x) =>
				String(x.depletionModel ?? "").trim().toLowerCase() ===
				String(rkey).trim().toLowerCase()
		);
		if (byKey) {
			return {
				depletionModelID:
					byKey.depletionModelID ?? byKey.DepletionModelID,
				depletionModel: byKey.depletionModel,
			};
		}
	}
	return {
		depletionModelID: rid ?? "",
		depletionModel: String(rkey ?? ""),
	};
}

/** Stable key for matching breakdown rows across Allocate refreshes (API payload or grid shape). */
export function allocateGridRowMergeKey(row) {
	const seg = (v) =>
		v === undefined || v === null ? "" : String(v).trim();

	const cid = seg(row?.customerID ?? row?.CustomerID);
	const cgid = seg(row?.customerGroupID ?? row?.CustomerGroupID);
	const pgid = seg(row?.productGroupID ?? row?.ProductGroupID);
	const skid = seg(row?.skuid ?? row?.SKUID);

	if (cid || cgid || pgid || skid) {
		return `${cid}|${cgid}|${pgid}|${skid}`;
	}
}

/**
 * Resolves row depletion using optional allocate snapshot (persist checked),
 * else depletionModelName → previousDepletionModel → quota-level defaults via list.
 */
function resolveGridRowDepletion(
	dmList,
	item,
	timeDepletionDefaults,
	forceTimeDepletionOnly,
	persistSnapshotByKey
) {
	const fromListByQuotaDefaults = () => {
		if (!timeDepletionDefaults) {
			return { depletionModelID: "", depletionModel: "" };
		}
		const qid = timeDepletionDefaults.depletionModelID;
		const qkey = timeDepletionDefaults.depletionModel;
		if (dmList?.length) {
			if (gridDepletionHasValue(qid)) {
				const byId = dmList.find(
					(x) =>
						Number(x.depletionModelID ?? x.DepletionModelID) === Number(qid)
				);
				if (byId) {
					return {
						depletionModelID:
							byId.depletionModelID ?? byId.DepletionModelID,
						depletionModel: byId.depletionModel,
					};
				}
			}
			if (gridDepletionHasValue(qkey)) {
				const byKey = dmList.find(
					(x) =>
						String(x.depletionModel ?? "").trim().toLowerCase() ===
						String(qkey).trim().toLowerCase()
				);
				if (byKey) {
					return {
						depletionModelID:
							byKey.depletionModelID ?? byKey.DepletionModelID,
						depletionModel: byKey.depletionModel,
					};
				}
			}
		}
		return {
			depletionModelID: timeDepletionDefaults.depletionModelID ?? "",
			depletionModel: timeDepletionDefaults.depletionModel ?? "",
		};
	};

	if (forceTimeDepletionOnly) {
		return fromListByQuotaDefaults();
	}

	if (persistSnapshotByKey instanceof Map && persistSnapshotByKey.size > 0) {
		const snapRow =
			persistSnapshotByKey.get(allocateGridRowMergeKey(item)) ?? null;
		const fromSnap = normalizePersistedBreakdownRowDepletion(dmList, snapRow);
		if (fromSnap) {
			return fromSnap;
		}
	}

	const dmName = String(
		item.depletionModelName ?? item.DepletionModelName ?? ""
	).trim();
	if (gridDepletionHasValue(dmName) && dmList?.length) {
		const byName = dmList.find(
			(x) =>
				String(x.depletionModelName ?? "").trim().toLowerCase() ===
				dmName.toLowerCase()
		);
		if (byName) {
			return {
				depletionModelID:
					byName.depletionModelID ?? byName.DepletionModelID,
				depletionModel: byName.depletionModel,
			};
		}
	}

	const prevDm = String(
		item.previousDepletionModel ?? item.PreviousDepletionModel ?? ""
	).trim();
	if (gridDepletionHasValue(prevDm) && dmList?.length) {
		const byPrev = dmList.find(
			(x) =>
				String(x.depletionModel ?? "").trim().toLowerCase() ===
				prevDm.toLowerCase()
		);
		if (byPrev) {
			return {
				depletionModelID:
					byPrev.depletionModelID ?? byPrev.DepletionModelID,
				depletionModel: byPrev.depletionModel,
			};
		}
	}

	return fromListByQuotaDefaults();
}

export function prepareGridData(
	data,
	initialForecast,
	timeDepletionDefaults = null,
	options = {}
) {
	const forceTimeDepletionOnly = options.forceTimeDepletionOnly === true;
	if (data) {
		const dmList = options.depletionModelsList;
		const persistRows = options.persistAllocateBreakdownSnapshot;
		const persistSnapshotByKey =
			Array.isArray(persistRows) && persistRows.length > 0
				? new Map(
						persistRows.map((r) => [
							allocateGridRowMergeKey(r),
							r,
						])
					)
				: null;
		const rows = data.map((item, index) => {
			const { depletionModelID, depletionModel } = resolveGridRowDepletion(
				dmList,
				item,
				timeDepletionDefaults,
				forceTimeDepletionOnly,
				persistSnapshotByKey
			);
			const depletionModelName = dmList.find(x => x.depletionModelID === depletionModelID)?.depletionModelName;

			return {
				id: index,
				quotaID: item.quotaID,
				customerQuotaID: item.customerQuotaID,
				skuid: item.skuid,
				skuName: item.skuName,
				skuNumb: item.skuNumb,
				productGroupID: item.productGroupID,
				packID: item.packID,
				packName: item.packName,
				skuType: item.skuType,
				customerGroupID: item.customerGroupID,
				customerGroupName: item.customerGroupName,
				customerNumber: item.customerNumber,
				type: item.type,
				unquieID: item.unquieID,
				ID: item.id, // actual prop is id from the API.
				name: item.name,
				customerID: item.customerID,
				customerName: item.customerName,
				initialQuota: item.initialQuota,
				sharePercentage: item.sharePercentage,
				customerSharePercentage: item.customerSharePercentage,
				customerPercentageExact: item.customerPercentageExact,
				safetyMargin: item.safetyMargin,
				tender: item.tender,
				adjustment: item.adjustment,
				weekly1: !item.weekly1 ? 0 : item.weekly1,
				weekly2: !item.weekly2 ? 0 : item.weekly2,
				weekly3: !item.weekly3 ? 0 : item.weekly3,
				weekly4: !item.weekly4 ? 0 : item.weekly4,
				weekly5: !item.weekly5 ? 0 : item.weekly5,
				weekly1Per: item.weekly1Per,
				weekly2Per: item.weekly2Per,
				weekly3Per: item.weekly3Per,
				weekly4Per: item.weekly4Per,
				weekly5Per: item.weekly5Per,
				quota: item.quota,
				sit: item.sit,
				customerQuotaComments: item.customerQuotaComments,
				totalQuota: item.totalQuota,
				minimumQuota: item.minimumQuota,
				customerTrustLevel: item.customerTrustLevel,
				q1Ratio: item.q1Ratio,
				q2Ratio: item.q2Ratio,
				unusedQuota: item.unusedQuota,
				isBuffer: item.isBuffer,
				skuPercentage: item.skuPercentage,
				initialForecast,
				hasError: false,
				depletionModelID,
				depletionModel,
				depletionModelName: depletionModelName,
			};
		});
		// console.log("Define_defineUtils_prepareGridData", rows);
		return rows;
	}
	return [];
}

export function addWeeklyDepletionValuesToGrid(
	data,
	valuesList,
	selectedDepletionPeriodType
) {
	if (!data) return [];
	const rows = data.map((item) => {
		const dm = rowDepletionModelKey(item);
		if (!dm) {
			return { ...item };
		}
		if (dm === "none") {
			return { ...item, ...NONE_DEPLETION_GRID_WEEK_PERCENTS };
		}
		const wc = getRowWeekPercents(
			valuesList,
			selectedDepletionPeriodType,
			item
		);
		return {
			...item,
			weekly1Per: wc.week1,
			weekly2Per: wc.week2,
			weekly3Per: wc.week3,
			weekly4Per: wc.week4,
			weekly5Per: wc.week5,
		};
	});
	return rows;
}

export function getAllCustomerTypes(list) {
	if (list) {
		const types = list.map((item, index) => {
			return item.type;
		});
		const customerTypes = Array.from(new Set(types));
		return customerTypes;
	}
	return [];
}

export function getAllSKUList(list) {
	if (list) {
		const skus = list
			.map((item) => ({
				skuName: item.skuName,
				//skuNumber: item.skuType ==="PG"? item.productGroupID : item.skuNumb
				skuNumber: item.skuNumb,
			}))
			.filter(
				(value, index, self) =>
					index ===
					self.findIndex(
						(t) =>
							t.skuName === value.skuName && t.skuNumber === value.skuNumber
					)
			);

		const skuList = Array.from(new Set(skus));
		return skuList;
	}
}

export function getAllCustomerTrustLevels(list) {
	if (list) {
		const levels = list.map((item, index) => {
			return item.customerTrustLevel;
		});
		const custLevels = Array.from(new Set(levels));
		return custLevels;
	}
	return [];
}

/**
 * Per customer/SKU export flag: true when period is 4-week globally, or when the row is a weekly
 * depletion curve with zero Week5 percent (matches Define ExportExcel payload).
 */
export function rowIsWeek4OnlyForExport(row, globalIsWeek4Only) {
	if (!row) return Boolean(globalIsWeek4Only);
	const dmKeyLower = String(row.depletionModel ?? "").trim().toLowerCase();
	const isNoneOrBiweekly =
		dmKeyLower === "none" || dmKeyLower === "biweekly";
	const weekly5Per = Number(row.weekly5Per ?? 0);
	return (
		Boolean(globalIsWeek4Only) ||
		(!globalIsWeek4Only && !isNoneOrBiweekly && weekly5Per === 0)
	);
}

/**
 * Define ExportExcel / ExportFile payloads: for mixed weekly sheets, none rows send null weeks 2–5;
 * biweekly rows send null weeks 3–5 (server renders empty cells).
 */
export function applyWeeklyBucketsExportMask(depletionModelKey, weeklyShape) {
	const k = String(depletionModelKey ?? "").trim().toLowerCase();
	const numOrNull = (w) => {
		if (w === undefined || w === null || w === "") return null;
		const n = Number(w);
		return Number.isNaN(n) ? null : n;
	};
	const w1 = numOrNull(
		weeklyShape?.weekly1 ?? weeklyShape?.Weekly1
	);
	const w2 = numOrNull(
		weeklyShape?.weekly2 ?? weeklyShape?.Weekly2
	);
	const w3 = numOrNull(
		weeklyShape?.weekly3 ?? weeklyShape?.Weekly3
	);
	const w4 = numOrNull(
		weeklyShape?.weekly4 ?? weeklyShape?.Weekly4
	);
	const w5 = numOrNull(
		weeklyShape?.weekly5 ?? weeklyShape?.Weekly5
	);
	if (k === "none") {
		return {
			weekly1: w1,
			weekly2: null,
			weekly3: null,
			weekly4: null,
			weekly5: null,
		};
	}
	if (k === "biweekly") {
		return {
			weekly1: w1,
			weekly2: w2,
			weekly3: null,
			weekly4: null,
			weekly5: null,
		};
	}
	return {
		weekly1: w1,
		weekly2: w2,
		weekly3: w3,
		weekly4: w4,
		weekly5: w5,
	};
}

/** True when Weekly scenario hides row-level Time Depletion in exports (matches Define grid columns). */
export function scenarioExportOmitsRowDepletionType(selectedScenario) {
	return selectedScenario?.periodType === "Weekly";
}

/** Row payload for Define/ExportFile — merges grid depletion when server row lacks it. */
export function mergeExportQuotaCustomerRow(
	item,
	gridData,
	globalIsWeek4Only,
	omitRowDepletionTypeForExport = false
) {
	const g =
		gridData?.find(
			(row) =>
				row.skuid === item.skuid &&
				row.customerID === item.customerID &&
				row.customerGroupID === item.customerGroupID &&
				row.productGroupID === item.productGroupID
		) ?? null;
	const depletionModel = item.depletionModel ?? g?.depletionModel ?? "";
	const weekly5Per = Number(g?.weekly5Per ?? item.weekly5Per ?? 0);
	const merged = {
		adjustment: item.adjustment,
		customerGroupID: item.customerGroupID,
		customerNumber: item.customerNumber,
		customerQuotaComments: item.customerQuotaComments,
		initialQuota: item.initialQuota,
		name: item.name,
		packName: item.packName,
		quota: item.quota,
		safetyMargin: item.safetyMargin,
		skuNumb: item.skuNumb,
		unusedQuota: item.unusedQuota,
		weekly1: item.weekly1,
		weekly2: item.weekly2,
		weekly3: item.weekly3,
		weekly4: item.weekly4,
		weekly5: item.weekly5,
		tender: item.tender,
		isBuffer: item.isBuffer,
		sit: item.sit,
		isWeek4Only: rowIsWeek4OnlyForExport(
			{ depletionModel, weekly5Per },
			globalIsWeek4Only
		),
	};
	if (!omitRowDepletionTypeForExport) {
		merged.depletionModel = depletionModel;
		merged.depletionModelName = item.depletionModelName ?? g?.depletionModelName ?? "";
	}
	const masked = applyWeeklyBucketsExportMask(depletionModel, merged);
	merged.weekly1 = masked.weekly1;
	merged.weekly2 = masked.weekly2;
	merged.weekly3 = masked.weekly3;
	merged.weekly4 = masked.weekly4;
	merged.weekly5 = masked.weekly5;
	return merged;
}

export function prepareCustomers(gridData, gridDataFiltered) {
	//  console.log("prepareCustomers funtion started at ", new Date().toISOString());
	let customers = [];
	// let gridData = _.cloneDeep(mainList);
	// let gridDataFiltered = _.cloneDeep(filteredList);
	let map = {};
	for (let i = 0; i < gridDataFiltered.length; ++i) {
		let item = gridDataFiltered[i];
		map[item.id] = item;
	}
	for (let i = 0; i < gridData.length; ++i) {
		const gridItem = gridData[i];
		const filGridItem = map[gridItem.id];
		// console.log("filGridItem", filGridItem);
		if (filGridItem) {
			customers.push(filGridItem);
		} else {
			customers.push(gridItem);
		}
	}
	//  console.log("prepareCustomers funtion ended at ", new Date().toISOString());
	return customers;
}

/** @param fourWeekCalendar True when period is 4-week (only weeks 1–4 validated; no weekly4 ≤ weekly5 check). */
export function validateCustomers(customers, depletionFallback, fourWeekCalendar) {
	let list = [];
	const fb =
		depletionFallback != null && depletionFallback !== ""
			? String(depletionFallback).trim().toLowerCase()
			: "";

	const fourWeekPeriod = Boolean(fourWeekCalendar);

	for (let i = 0; i < customers.length; ++i) {
		const customer = customers[i];
		let notNull = true;
		let validValues = true;

		const dm = rowDepletionModelKey(customer);
		const dk = dm !== "" ? dm : fb;

		if (dk === "" || dk === "none") {
			// No weekly shape validation for none / missing model (matches legacy global None).
		} else if (dk === "biweekly") {
			notNull =
				customer.weekly1 !== null &&
				customer.weekly1 !== undefined &&
				!isNaN(customer.weekly1) &&
				customer.weekly2 !== null &&
				customer.weekly2 !== undefined &&
				!isNaN(customer.weekly2);

			validValues = customer.weekly1 <= customer.weekly2;
		} else if (fourWeekPeriod) {
			notNull =
				customer.weekly1 !== null &&
				customer.weekly1 !== undefined &&
				!isNaN(customer.weekly1) &&
				customer.weekly2 !== null &&
				customer.weekly2 !== undefined &&
				!isNaN(customer.weekly2) &&
				customer.weekly3 !== null &&
				customer.weekly3 !== undefined &&
				!isNaN(customer.weekly3) &&
				customer.weekly4 !== null &&
				customer.weekly4 !== undefined &&
				!isNaN(customer.weekly4);
			validValues =
				customer.weekly1 <= customer.weekly2 &&
				customer.weekly2 <= customer.weekly3 &&
				customer.weekly3 <= customer.weekly4;
		} else {
			notNull =
				customer.weekly1 !== null &&
				customer.weekly1 !== undefined &&
				!isNaN(customer.weekly1) &&
				customer.weekly2 !== null &&
				customer.weekly2 !== undefined &&
				!isNaN(customer.weekly2) &&
				customer.weekly3 !== null &&
				customer.weekly3 !== undefined &&
				!isNaN(customer.weekly3) &&
				customer.weekly4 !== null &&
				customer.weekly4 !== undefined &&
				!isNaN(customer.weekly4) &&
				customer.weekly5 !== null &&
				customer.weekly5 !== undefined &&
				!isNaN(customer.weekly5);
			validValues =
				customer.weekly1 <= customer.weekly2 &&
				customer.weekly2 <= customer.weekly3 &&
				customer.weekly3 <= customer.weekly4 &&
				customer.weekly4 <= customer.weekly5;
		}
		if (!notNull || !validValues) {
			// console.log(
			// 	"validateCustomers funtion ended with validation error at ",
			// 	new Date().toISOString()
			// );
			return `Invalid value given for ${
				customer.customerName
					? customer.customerName
					: customer.customerGroupName
			}`;
		}
		list.push({
			customerGroupID: customer.customerGroupID,
			customerID: customer.customerID,
			initialQuota: customer.initialQuota,
			safetyMargin: customer.safetyMargin,
			unusedQuota: customer.unusedQuota,
			tender: customer.tender,
			adjustment: customer.adjustment,
			weekly1: !customer.weekly1 ? 0 : customer.weekly1,
			weekly2: !customer.weekly2 ? 0 : customer.weekly2,
			weekly3: !customer.weekly3 ? 0 : customer.weekly3,
			weekly4: !customer.weekly4 ? 0 : customer.weekly4,
			weekly5: !customer.weekly5 ? 0 : customer.weekly5,
			customerQuotaComments: customer.customerQuotaComments,
			skuid: customer.skuid,
			productGroupID: customer.productGroupID,
			quota: customer.quota,
			depletionModelID:
				customer.depletionModelID != null &&
				customer.depletionModelID !== ""
					? Number(customer.depletionModelID)
					: null,
		});
	}
	//  console.log("validateCustomers funtion ended at ", new Date().toISOString());
	// console.log("list", list);
	return list;
}

export function prepareList1(customers) {
	let list1 = customers.map((item, index) => {
		return {
			quotaID: item.quotaID,
			customerQuotaID: item.customerQuotaID,
			skuid: item.skuid,
			skuName: item.skuName,
			skuNumb: item.skuNumb,
			productGroupID: item.productGroupID,
			packID: item.packID,
			packName: item.packName,
			skuType: item.skuType,
			customerGroupID: item.customerGroupID,
			customerGroupName: item.customerGroupName,
			customerNumber: item.customerNumber,
			type: item.type,
			unquieID: item.unquieID,
			id: item.ID,
			name: item.name,
			customerID: item.customerID,
			customerName: item.customerName,
			initialQuota: item.initialQuota,
			sharePercentage: item.sharePercentage,
			customerSharePercentage: item.customerSharePercentage,
			customerPercentageExact: item.customerPercentageExact,
			safetyMargin: item.safetyMargin,
			tender: item.tender,
			adjustment: item.adjustment,
			weekly1: !item.weekly1 ? 0 : item.weekly1,
			weekly2: !item.weekly2 ? 0 : item.weekly2,
			weekly3: !item.weekly3 ? 0 : item.weekly3,
			weekly4: !item.weekly4 ? 0 : item.weekly4,
			weekly5: !item.weekly5 ? 0 : item.weekly5,
			weekly1Per: item.weekly1Per,
			weekly2Per: item.weekly2Per,
			weekly3Per: item.weekly3Per,
			weekly4Per: item.weekly4Per,
			weekly5Per: item.weekly5Per,
			quota: item.quota,
			sit: item.sit,
			customerQuotaComments: item.customerQuotaComments,
			totalQuota: item.totalQuota,
			minimumQuota: item.minimumQuota,
			customerTrustLevel: item.customerTrustLevel,
			q1Ratio: item.q1Ratio,
			q2Ratio: item.q2Ratio,
			unusedQuota: item.unusedQuota,
			isBuffer: item.isBuffer,
			skuPercentage: item.skuPercentage,
		};
	});
	return list1;
}

export function prepareList2(customers) {
	// console.log("prepareList2 funtion started at", new Date().toISOString());
	let list2 = customers.map((item, index) => {
		// console.log("prepareList2_item", item);
		return {
			customerGroupID: item.customerGroupID,
			customerID: item.customerID,
			initialQuota: item.initialQuota,
			safetyMargin: item.safetyMargin,
			unusedQuota: item.unusedQuota,
			tender: item.tender,
			adjustment: item.adjustment,
			weekly1: !item.weekly1 ? 0 : item.weekly1,
			weekly2: !item.weekly2 ? 0 : item.weekly2,
			weekly3: !item.weekly3 ? 0 : item.weekly3,
			weekly4: !item.weekly4 ? 0 : item.weekly4,
			weekly5: !item.weekly5 ? 0 : item.weekly5,
			customerQuotaComments: item.customerQuotaComments,
			skuid: item.skuid,
			productGroupID: item.productGroupID,
			quota: item.quota,
		};
	});
	// console.log("prepareList2 funtion ended at", new Date().toISOString());
	return list2;
}

export function getQuotaTableData(columns, data) {
	// console.log(columns, data);
	const cols = getQuotaTableColumns(data);
	const rows = getQuotaTableRows(data, columns);
	return { rows, cols };
}

function getQuotaTableColumns(data) {
	let columns = [
		{ field: "data", headerName: "Data Source", width: 120, pinned: "left" },
	];

	let counts = {};
	let rowData = [];

	data.forEach((row, index) => {
		let date = row[0].split(",")[0];
		if (counts[date] !== undefined) {
			counts[date] += 1;
			rowData.push(`${date}_${counts[date]}`);
		} else {
			counts[date] = 0;
			rowData.push(row[0].split(",")[0]);
		}
	});

	// data.forEach((row, index) => {
	// 	console.log(`${index}: ${JSON.stringify(row)}`);
	// });

	rowData.forEach((array, index) => {
		const colName = array;
		const col = {
			field: colName,
			headerName: colName.split("_")[0],
			disableColumnMenu: true,
			filterable: false,
			hideSortIcons: true,
			width: 90,
			valueFormatter: (params) => {
				const formattedValue = params.value?.toLocaleString('en-US');
				return formattedValue;
			},
		};
		columns.push(col);
	});
	// console.log("getQuotaTableColumns", columns);
	return columns;
}

function getQuotaTableRows(data, columns) {
	let newData = [];
	data.forEach((array) => {
		const nestedArray = array[0].split(",");
		newData.push(nestedArray);
	});

	let counts = {};
	newData.forEach((row, index) => {
		let date = row[0];
		if (counts[date] !== undefined) {
			counts[date] += 1;
			row[0] = `${date}_${counts[date]}`;
		} else {
			counts[date] = 0;
		}
	});
	// console.log("getQuotaTableRows_newData", newData);

	let rows = [];
	let id = 1;

	for (let i = 1; i < columns.length; ++i) {
		const Data = columns[i];
		let row = { id };
		row = { ...row, data: Data };
		++id;

		for (let j = 0; j < newData.length; ++j) {
			if (newData[j][i] === "null") {
				row[newData[j][0]] = "";
			} else {
				row[newData[j][0]] = newData[j][i];
			}
		}
		rows.push(row);
	}
	// console.log("getQuotaTableRows_rows", rows);
	return rows;
}

export function formatNumber(num) {
	// console.log("Input ", num);
	let numStr = Math.abs(num).toString();
	let len = numStr.length;
	if (len <= 3) {
		return num.toString(); // No formatting needed for numbers <= 999
	}
	let output = "";
	const positions = [2, 4, 6];
	let p = 0;
	for (let i = numStr.length - 1; i >= 0; --i) {
		if (positions.includes(p) && i !== 0) {
			output = "," + numStr[i] + output;
		} else {
			output = numStr[i] + output;
		}
		++p;
	}
	// console.log("Output ", output);
	return output;
}

export function removeComma(str) {
	let formattedString = "";
	for (let i = 0; i < str.length; i++) {
		if (str[i] !== ",") {
			formattedString += str[i];
		}
	}
	return formattedString;
}

/** Empty or digits-only (commas stripped) — for rounder value fields. */
export function isDigitsOnlyNumericInput(value) {
	const normalized = removeComma(String(value ?? "").trim());
	return normalized === "" || /^\d+$/.test(normalized);
}

export function hasNaNOverBoxRoundValues(overBoxRoundValues) {
	return Object.values(overBoxRoundValues ?? {}).some(
		(value) =>
			value != null &&
			value !== "" &&
			Number.isNaN(Number(value))
	);
}

export const sizes = {
	w650: 650,
	w450: 450,
	w300: 300,
	w270: 270,
	w250: 250,
	w200: 200,
	w150: 150,
	h35: 35,
};
